// eslint-disable-next-line
import React, { useEffect, useRef, useState } from 'react';
import { GoogleMap, InfoWindow, Marker } from '@react-google-maps/api';
import PinReadyActive from '../../assets/images/map/Pin_Ready_Active.svg';
import { BuildingListMarker } from '../../types/building';
import PinWarningActive from '../../assets/images/map/Pin_Warning_Active.svg';
import PinOfflineActive from '../../assets/images/map/Pin_Offline_Active.svg';
import PinOfflineInactive from '../../assets/images/map/Pin_Offline_Inactive.svg';
import PinErrorActive from '../../assets/images/map/Pin_Error_Active.svg';
import PinBlockedActive from '../../assets/images/map/Pin_Blocked_Active.svg';
import { useLanguageLocale } from '../../hooks/language-locale-hooks';
import { CircularProgress, Typography } from '@mui/material';
import { StatusTag } from '../StatusTag/StatusTag';
import { Status } from '../../types';
import { EmptyState } from '@brightlayer-ui/react-components';
import { DEVICE_STATUS } from '../../app/enum/ENUM';
import { Fullscreen, Layers, MyLocation, ZoomIn, ZoomOut } from '@mui/icons-material';
import { IconButton } from '@mui/material';

type Props = {
    buildings: BuildingListMarker;
    buildingId: string;
    OnBuidlingMarkerClicked: Function;
    mapContainerStyle?: { height: string; width: string };
    useInactiveOfflineMarker?: boolean;
    showCustomControls?: boolean;
};

const MapContainer = (props: Props) => {
    const { t } = useLanguageLocale();
    const [isDataLoaded, setIsDataLoaded] = useState(false);
    const [center, setCenter] = React.useState<google.maps.LatLngLiteral>();
    const [zoom, setZoom] = useState(9);
    const [activeMarker, setActiveMarker] = useState(null);
    const [markersList, setMarkersList] = useState<BuildingListMarker>(props.buildings);
    const [isLoadState, setIsLoad] = useState(false);
    const [mapRef, setMapRef] = useState<google.maps.Map | null>(null);
    const [mapTypeId, setMapTypeId] = useState<google.maps.MapTypeId | undefined>(undefined);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const customMapWrapperRef = useRef<HTMLDivElement | null>(null);

    const handleActiveMarker = (marker: any, position: any) => {
        setCenter(position);
        if (marker === activeMarker) {
            return;
        }
        setActiveMarker(marker);
    };

    const handleActiveMarkerClicked = (id: string, parentProjectId: string) => {
        props.OnBuidlingMarkerClicked(id, parentProjectId);
    }

    useEffect(() => {
        setMarkersList(props.buildings);
        if (props.buildings.length > 0)
            setIsDataLoaded(true);
    }, [props.buildings]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (isDataLoaded) {
            if (props.buildingId !== '' && markersList.length > 0) {
                setCenter(
                    markersList.filter(function (item) {
                        return item.id === props.buildingId;
                    })[0].position
                );
                setZoom(9);
            }
            setIsLoad(true);
        }
    }, [isDataLoaded, props.buildings]); // eslint-disable-line react-hooks/exhaustive-deps

    // Set Marker icon according to status of building

    const getMarkerIcon = (status: number) => {
        switch (status) {
            case DEVICE_STATUS.STATUS_READY:
                return PinReadyActive;
            case DEVICE_STATUS.STATUS_WARNING:
                return PinWarningActive;
            case DEVICE_STATUS.STATUS_BLOCK:
                return PinBlockedActive;
            case DEVICE_STATUS.STATUS_OFFLINE:
                return props.useInactiveOfflineMarker ? PinOfflineInactive : PinOfflineActive;
            case DEVICE_STATUS.STATUS_OFFLINE_DISABLE:
                return PinOfflineInactive;
            case DEVICE_STATUS.STATUS_ERROR:
                return PinErrorActive;
            default:
                return PinOfflineInactive;
        }
    };

    const getStatus = (status: number) => {
        switch (status) {
            case DEVICE_STATUS.STATUS_READY:
                return Status.Ready;
            case DEVICE_STATUS.STATUS_WARNING:
                return Status.Warning;
            case DEVICE_STATUS.STATUS_BLOCK:
                return Status.Block;
            case DEVICE_STATUS.STATUS_OFFLINE:
                return Status.Offline;
            case DEVICE_STATUS.STATUS_OFFLINE_DISABLE:
                return Status.Offline;
            case DEVICE_STATUS.STATUS_ERROR:
                return Status.Error;
            default:
                return Status.InActive;
        }
    };

    const getAdjustedMarkersList = (): BuildingListMarker => {
        const markerCountByPosition = new Map<string, number>();
        return markersList.map((marker) => {
            const key = `${marker.position.lat.toFixed(6)}:${marker.position.lng.toFixed(6)}`;
            const duplicateIndex = markerCountByPosition.get(key) || 0;
            markerCountByPosition.set(key, duplicateIndex + 1);

            if (duplicateIndex === 0) {
                return marker;
            }

            const offset = duplicateIndex * 0.0002;
            return {
                ...marker,
                position: {
                    lat: marker.position.lat + offset,
                    lng: marker.position.lng + offset,
                },
            };
        });
    };

    // // Iterate myPlaces to size, center, and zoom map to contain all markers
    const fitBounds = (map: { fitBounds: (arg0: google.maps.LatLngBounds) => void }) => {
        const bounds = new window.google.maps.LatLngBounds();
        markersList.map((item) => {
            bounds.extend(item.position);
            return item.id;
        });
        map.fitBounds(bounds);
    };

    const loadHandler = (map: any) => {
        setMapRef(map);
        // Fit map bounds to contain all markers
        fitBounds(map);
        var listener = google.maps.event.addListener(map, "idle", function () {
            if (map.getZoom() > 12) map.setZoom(12);
            google.maps.event.removeListener(listener);
        });
    };

    const mapMarkers = getAdjustedMarkersList();

    const locateActiveMarker = (): void => {
        if (!mapRef) {
            return;
        }
        if (activeMarker) {
            const selectedMarker = mapMarkers.find((marker) => marker.id === activeMarker);
            if (selectedMarker) {
                mapRef.panTo(selectedMarker.position);
                mapRef.setZoom(Math.max(mapRef.getZoom() || zoom, 10));
                return;
            }
        }
        if (mapMarkers.length > 0) {
            mapRef.panTo(mapMarkers[0].position);
            mapRef.setZoom(Math.max(mapRef.getZoom() || zoom, 9));
        }
    };

    const toggleMapLayer = (): void => {
        if (!mapRef) {
            return;
        }
        const googleMapTypes = window.google?.maps?.MapTypeId;
        if (!googleMapTypes) {
            return;
        }

        const currentType = mapTypeId || googleMapTypes.ROADMAP;
        const nextType = currentType === googleMapTypes.ROADMAP
            ? googleMapTypes.SATELLITE
            : googleMapTypes.ROADMAP;
        setMapTypeId(nextType);
        mapRef.setMapTypeId(nextType);
    };

    const toggleFullscreen = (): void => {
        const mapContainerElement = customMapWrapperRef.current;
        if (!mapContainerElement) {
            return;
        }
        if (document.fullscreenElement) {
            document.exitFullscreen();
            return;
        }
        mapContainerElement.requestFullscreen();
    };

    useEffect(() => {
        const onFullscreenChange = (): void => {
            setIsFullscreen(document.fullscreenElement === customMapWrapperRef.current);
        };

        document.addEventListener('fullscreenchange', onFullscreenChange);
        return () => {
            document.removeEventListener('fullscreenchange', onFullscreenChange);
        };
    }, []);

    useEffect(() => {
        if (!mapRef) {
            return;
        }
        window.setTimeout(() => {
            google.maps.event.trigger(mapRef, 'resize');
            if (mapMarkers.length > 0) {
                mapRef.panTo(mapMarkers[0].position);
            }
        }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFullscreen, mapRef]);

    const zoomIn = (): void => {
        if (!mapRef) {
            return;
        }
        const currentZoom = mapRef.getZoom() || zoom;
        mapRef.setZoom(currentZoom + 1);
    };

    const zoomOut = (): void => {
        if (!mapRef) {
            return;
        }
        const currentZoom = mapRef.getZoom() || zoom;
        mapRef.setZoom(currentZoom - 1);
    };

    const mapContainerStyle = props.showCustomControls && isFullscreen
        ? { height: '100vh', width: '100vw' }
        : (props.mapContainerStyle || { height: '100vh', width: '100%' });

    return (
        <>
            {!isDataLoaded && (
                <div style={{ height: '100vh', width: '100%' }}>
                    <EmptyState icon={<CircularProgress id="progress-spinner" />} title="" placeholder={undefined} />
                </div>
            )}

            {isDataLoaded && isLoadState && !props.showCustomControls && (
                <GoogleMap
                    onLoad={loadHandler}
                    center={center}
                    zoom={zoom}
                    onClick={() => setActiveMarker(null)}
                    mapContainerStyle={{ height: '100vh', width: '100%' }}
                    options={{ streetViewControl: false, zoomControlOptions: { position: 9 } }}
                    data-testid="google-map-container"
                >
                    {markersList.map(({ id, name, position, elStatus, city, address, systemCount, parentProjectId }) => (
                        <Marker
                            key={id}
                            position={position}
                            onClick={() => handleActiveMarker(id, position)}
                            icon={getMarkerIcon(elStatus)}
                            data-testid="marker-selected"
                        >
                            {activeMarker === id ? (
                                <InfoWindow onCloseClick={() => setActiveMarker(null)} data-testid="marker-details">
                                    <div>
                                        <Typography variant="body2" style={{ fontSize: 16, cursor: 'pointer' }} onClick={() => handleActiveMarkerClicked(id, parentProjectId)}>
                                            <b>{name}</b>
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            style={{ fontSize: 14 }}
                                            id="system-count"
                                            data-testid="project-name"
                                        >
                                            {systemCount} {systemCount > 1 ? t('MAP.SYSTEMS') : t('MAP.SYSTEM')}
                                        </Typography>
                                        <Typography variant="body2" style={{ fontSize: 14 }} id="marker-location">
                                            {address}, {city}
                                        </Typography>
                                        <StatusTag
                                            status={getStatus(elStatus).toString() || Status.InActive}
                                            style={{ marginRight: 5 }}
                                        />
                                    </div>
                                </InfoWindow>
                            ) : null}
                        </Marker>
                    ))}
                </GoogleMap>
            )}

            {isDataLoaded && isLoadState && props.showCustomControls && (
                <div id="custom-map-wrapper" ref={customMapWrapperRef} style={{ position: 'relative', height: '100%', width: '100%' }}>
                    <GoogleMap
                        onLoad={loadHandler}
                        center={center}
                        zoom={zoom}
                        onClick={() => setActiveMarker(null)}
                        mapContainerStyle={mapContainerStyle}
                        options={{
                            streetViewControl: false,
                            zoomControl: false,
                            fullscreenControl: false,
                            mapTypeControl: true,
                            mapTypeControlOptions: {
                                style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
                                position: google.maps.ControlPosition.TOP_LEFT,
                            }
                        }}
                        data-testid="google-map-container"
                    >
                        {mapMarkers.map(({ id, name, position, elStatus, city, address, systemCount, parentProjectId }) => (
                            <Marker
                                key={id}
                                position={position}
                                onClick={() => handleActiveMarker(id, position)}
                                icon={getMarkerIcon(elStatus)}
                                data-testid="marker-selected"
                            >
                                {activeMarker === id ? (
                                    <InfoWindow onCloseClick={() => setActiveMarker(null)} data-testid="marker-details">
                                        <div>
                                            <Typography variant="body2" style={{ fontSize: 16, cursor: 'pointer' }} onClick={() => handleActiveMarkerClicked(id, parentProjectId)}>
                                                <b>{name}</b>
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                style={{ fontSize: 14 }}
                                                id="system-count"
                                                data-testid="project-name"
                                            >
                                                {systemCount} {systemCount > 1 ? t('MAP.SYSTEMS') : t('MAP.SYSTEM')}
                                            </Typography>
                                            <Typography variant="body2" style={{ fontSize: 14 }} id="marker-location">
                                                {address}, {city}
                                            </Typography>
                                            <StatusTag
                                                status={getStatus(elStatus).toString() || Status.InActive}
                                                style={{ marginRight: 5 }}
                                            />
                                        </div>
                                    </InfoWindow>
                                ) : null}
                            </Marker>
                        ))}
                    </GoogleMap>

                    <div
                        style={{
                            position: 'absolute',
                            right: 16,
                            bottom: 16,
                            display: 'flex',
                            gap: 8,
                            zIndex: 2,
                        }}
                    >
                        <div style={{ backgroundColor: '#FFFFFF', borderRadius: 4, boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>
                            <IconButton
                                size="small"
                                onClick={locateActiveMarker}
                                aria-label={t('MAP.LOCATE_ON_MAP')}
                                title={t('MAP.LOCATE_ON_MAP')}
                            >
                                <MyLocation fontSize="small" />
                            </IconButton>
                        </div>

                        <div style={{ backgroundColor: '#FFFFFF', borderRadius: 4, boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>
                            <IconButton
                                size="small"
                                onClick={toggleMapLayer}
                                aria-label={t('MAP.TOGGLE_MAP_LAYER')}
                                title={t('MAP.TOGGLE_MAP_LAYER')}
                            >
                                <Layers fontSize="small" />
                            </IconButton>
                        </div>

                        <div style={{ backgroundColor: '#FFFFFF', borderRadius: 4, boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>
                            <IconButton
                                size="small"
                                onClick={toggleFullscreen}
                                aria-label={t('MAP.TOGGLE_FULLSCREEN')}
                                title={t('MAP.TOGGLE_FULLSCREEN')}
                            >
                                <Fullscreen fontSize="small" />
                            </IconButton>
                        </div>

                        <div
                            style={{
                                backgroundColor: '#FFFFFF',
                                borderRadius: 4,
                                boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            <IconButton
                                size="small"
                                onClick={zoomOut}
                                aria-label={t('MAP.ZOOM_OUT')}
                                title={t('MAP.ZOOM_OUT')}
                            >
                                <ZoomOut fontSize="small" />
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={zoomIn}
                                aria-label={t('MAP.ZOOM_IN')}
                                title={t('MAP.ZOOM_IN')}
                            >
                                <ZoomIn fontSize="small" />
                            </IconButton>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default MapContainer;
